import { useEffect } from 'react';
import {
    makeStyles,
    tokens,
    Button,
    Spinner,
    Table,
    TableHeader,
    TableHeaderCell,
    TableBody,
    TableRow,
    TableCell,
} from '@fluentui/react-components';
import { ArrowSync24Regular, Delete24Regular, Open16Regular } from '@fluentui/react-icons';
import type { SearchIndexerContextValue } from '../../hooks/useSearchIndexer';
import type { InjioRegistrySource } from '@shared/dto/searchIndexer';

const useStyles = makeStyles({
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    heading: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        margin: 0,
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 24px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    rowCount: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: tokens.fontWeightSemibold,
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

interface Props {
    ctx: SearchIndexerContextValue;
}

export function CacheManagementPanel({ ctx }: Props) {
    const styles = useStyles();
    const {
        sources,
        cacheStats,
        cacheStatsLoading,
        refreshCacheStats,
        clearSourceCache,
        isRunning,
    } = ctx;

    // Auto-load stats on mount
    useEffect(() => {
        if (sources.length > 0) refreshCacheStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sources.length]);

    const handleClear = async (source: InjioRegistrySource) => {
        if (!confirm(`Clear all cached embeddings for "${source.displayName ?? source.listId}"? This cannot be undone.`)) return;
        await clearSourceCache(source);
    };

    const totalRows = Object.values(cacheStats).reduce((s, n) => s + n, 0);

    return (
        <div className={styles.card}>
            <div className={styles.topBar}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <h3 className={styles.heading}>Cache Management</h3>
                    {!cacheStatsLoading && totalRows > 0 && (
                        <span className={styles.hint}>{totalRows} total embedding rows</span>
                    )}
                </div>
                <Button
                    icon={cacheStatsLoading ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
                    appearance="subtle"
                    onClick={refreshCacheStats}
                    disabled={cacheStatsLoading || isRunning}
                >
                    Refresh Stats
                </Button>
            </div>

            {sources.length === 0 ? (
                <div className={styles.emptyState}>No sources registered.</div>
            ) : (
                <div className={styles.tableWrapper}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Source</TableHeaderCell>
                                <TableHeaderCell>Type</TableHeaderCell>
                                <TableHeaderCell>Cached Rows</TableHeaderCell>
                                <TableHeaderCell>Actions</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sources.map(source => {
                                const rowCount = cacheStats[source.listId];
                                return (
                                    <TableRow key={source.listId}>
                                        <TableCell>
                                            <strong>{source.displayName ?? source.listId}</strong>
                                        </TableCell>
                                        <TableCell>{source.sourceType}</TableCell>
                                        <TableCell>
                                            {cacheStatsLoading ? (
                                                <Spinner size="tiny" />
                                            ) : rowCount !== undefined ? (
                                                <span className={styles.rowCount}>{rowCount}</span>
                                            ) : (
                                                <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Button
                                                    icon={<Delete24Regular />}
                                                    appearance="subtle"
                                                    size="small"
                                                    onClick={() => handleClear(source)}
                                                    disabled={isRunning || !rowCount}
                                                >
                                                    Clear
                                                </Button>
                                                {source.cacheListUrl && (
                                                    <Button
                                                        as="a"
                                                        href={source.cacheListUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        icon={<Open16Regular />}
                                                        appearance="subtle"
                                                        size="small"
                                                        title="Open embeddings cache list in SharePoint"
                                                    >
                                                        Open in SharePoint
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
